/**
 * Customer ↔ helpdesk account matching.
 *
 * Matching only pre-fills the mapper. The administrator's saved links remain
 * the sole source of ticket ownership.
 */
import type { Account, CustomerAccountBinding } from '@/lib/api/helpdesk';

export interface MatchCustomer {
  customer_id: string;
  customer_name: string;
}

export type LinkOrigin = 'saved' | 'auto' | 'manual';

export interface CustomerLink {
  account_id: string;
  account_name: string;
  origin: LinkOrigin;
}

/** customer_id → linked account */
export type CustomerLinks = Record<string, CustomerLink>;

const LEGAL_SUFFIXES = new Set([
  'pty', 'ltd', 'limited', 'inc', 'incorporated', 'llc', 'cc', 'soc', 'plc', 'corp', 'corporation', 'co',
]);

/** Lowercase, strip diacritics, punctuation and legal suffixes, collapse whitespace. */
export function normaliseName(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(token => token && !LEGAL_SUFFIXES.has(token))
    .join(' ');
}

function groupByKey<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    if (!k) continue;
    groups.set(k, [...(groups.get(k) ?? []), item]);
  }
  return groups;
}

/**
 * Link each unlinked customer to the one account sharing its normalised name,
 * when that name is unique on both sides. Existing links are kept and their
 * accounts are never reassigned.
 */
export function autoMatch(customers: MatchCustomer[], accounts: Account[], existing: CustomerLinks): CustomerLinks {
  const links: CustomerLinks = { ...existing };
  const taken = new Set(Object.values(existing).map(link => link.account_id));
  const customerGroups = groupByKey(customers.filter(c => !links[c.customer_id]), c => normaliseName(c.customer_name));
  const accountGroups = groupByKey(accounts.filter(a => !taken.has(a.id)), a => normaliseName(a.name));

  for (const [key, matched] of customerGroups) {
    const candidates = accountGroups.get(key) ?? [];
    if (matched.length !== 1 || candidates.length !== 1) continue;
    links[matched[0].customer_id] = { account_id: candidates[0].id, account_name: candidates[0].name, origin: 'auto' };
  }
  return links;
}

/** API bindings for the customers that still exist in the directory. */
export function toBindings(links: CustomerLinks, customers: MatchCustomer[]): CustomerAccountBinding[] {
  const known = new Set(customers.map(customer => customer.customer_id));
  return Object.entries(links)
    .filter(([customer_id]) => known.has(customer_id))
    .map(([customer_id, link]) => ({ customer_id, account_id: link.account_id, account_name: link.account_name }));
}

/** Shared normalised tokens, weighted towards the rarer (more distinctive) side. */
function overlap(left: string, right: string): number {
  const a = new Set(normaliseName(left).split(' ').filter(Boolean));
  const b = new Set(normaliseName(right).split(' ').filter(Boolean));
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  a.forEach(token => { if (b.has(token)) shared += 1; });
  if (shared === 0) return 0;
  // Jaccard, nudged up when one name is contained in the other ("Allan Gray" in
  // "Allan Gray Propriety Limited"), which is how trading names usually differ.
  const union = new Set([...a, ...b]).size;
  const containment = shared === Math.min(a.size, b.size) ? 0.25 : 0;
  return shared / union + containment;
}

/**
 * Order accounts by how closely they resemble a customer's name.
 *
 * Suggestion only — nothing is linked without the administrator choosing it, so a
 * near miss costs a scroll rather than the wrong customer's tickets. Accounts that
 * share nothing are kept, alphabetically, after the candidates.
 */
export function rankAccounts(customerName: string, accounts: Account[]): Account[] {
  return [...accounts]
    .map(account => ({ account, score: overlap(customerName, account.name) }))
    .sort((left, right) =>
      right.score - left.score || left.account.name.localeCompare(right.account.name))
    .map(entry => entry.account);
}
